import { useState } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Button,
  Input,
  Label,
} from '@design-system';
import type { BenefitDesignInputs, CombinedFilterType } from '@/features/shop-insurance';
import type { BenefitDesignAvailableOptions } from '@/features/shop-insurance/hooks/use-benefit-design-quotes';

interface BenefitDesignFiltersProps {
  inputs: BenefitDesignInputs;
  availableOptions: BenefitDesignAvailableOptions;
  setMonthlyBenefit: (value: number | null) => void;
  setBenefitDuration: (value: CombinedFilterType | null) => void;
  setInflationProtection: (value: CombinedFilterType | null) => void;
}

function parseCurrency(raw: string): number | null {
  const n = Number(raw.replace(/[^0-9.]/g, ''));
  return isNaN(n) || n <= 0 ? null : n;
}

function parsePercent(raw: string): number | null {
  const stripped = raw.replace(/[^0-9.]/g, '');
  if (stripped === '') return null;
  const n = Number(stripped);
  return isNaN(n) || n < 0 || n > 100 ? null : n;
}

function isLifetime(raw: string): boolean {
  return raw.trim().toLowerCase() === 'lifetime';
}

function formatDurationDraft(value: number): string {
  return value === Infinity ? 'Lifetime' : String(value);
}

function parseDuration(raw: string): number | null {
  if (isLifetime(raw)) return Infinity;
  const n = Number(raw.replace(/[^0-9.]/g, ''));
  return isNaN(n) || n <= 0 ? null : n;
}

export function BenefitDesignFilters({
  inputs,
  setBenefitDuration,
  setInflationProtection,
  setMonthlyBenefit,
}: BenefitDesignFiltersProps) {
  // Local draft state — only committed to parent on Apply
  const [draftBenefit, setDraftBenefit] = useState(
    inputs.monthlyBenefit !== null ? String(inputs.monthlyBenefit) : '',
  );
  const [draftDurationType, setDraftDurationType] = useState<'combined' | 'separate'>(
    inputs.benefitDuration?.type ?? 'combined',
  );
  const [draftDuration, setDraftDuration] = useState(
    inputs.benefitDuration ? formatDurationDraft(inputs.benefitDuration.combined[0]) : '',
  );
  const [draftAobDuration, setDraftAobDuration] = useState(
    inputs.benefitDuration ? formatDurationDraft(inputs.benefitDuration.aob[0]) : '',
  );
  const [draftCobDuration, setDraftCobDuration] = useState(
    inputs.benefitDuration ? formatDurationDraft(inputs.benefitDuration.cob[0]) : '',
  );
  const [draftInflationType, setDraftInflationType] = useState<'combined' | 'separate'>(
    inputs.inflationProtection?.type ?? 'combined',
  );
  const [draftInflation, setDraftInflation] = useState(
    inputs.inflationProtection ? String(inputs.inflationProtection.combined[0]) : '',
  );
  const [draftAobInflation, setDraftAobInflation] = useState(
    inputs.inflationProtection ? String(inputs.inflationProtection.aob[0]) : '',
  );
  const [draftCobInflation, setDraftCobInflation] = useState(
    inputs.inflationProtection ? String(inputs.inflationProtection.cob[0]) : '',
  );

  function handleApply() {
    const benefit = parseCurrency(draftBenefit);
    setMonthlyBenefit(benefit);

    if (draftDurationType === 'combined') {
      const dur = parseDuration(draftDuration);
      setBenefitDuration(
        dur !== null
          ? { type: 'combined', combined: [dur, dur], aob: [dur, dur], cob: [dur, dur] }
          : null,
      );
    } else {
      const aob = parseDuration(draftAobDuration);
      const cob = parseDuration(draftCobDuration);
      if (aob !== null || cob !== null) {
        const aobRange: [number, number] = aob !== null ? [aob, aob] : [0, Infinity];
        const cobRange: [number, number] = cob !== null ? [cob, cob] : [0, Infinity];
        setBenefitDuration({
          type: 'separate',
          combined: [aobRange[0] + cobRange[0], aobRange[1] + cobRange[1]],
          aob: aobRange,
          cob: cobRange,
        });
      } else {
        setBenefitDuration(null);
      }
    }

    if (draftInflationType === 'combined') {
      const inf = parsePercent(draftInflation);
      setInflationProtection(
        inf !== null
          ? { type: 'combined', combined: [inf, inf], aob: [inf, inf], cob: [inf, inf] }
          : null,
      );
    } else {
      const aob = parsePercent(draftAobInflation);
      const cob = parsePercent(draftCobInflation);
      if (aob !== null || cob !== null) {
        const aobRange: [number, number] = aob !== null ? [aob, aob] : [0, 100];
        const cobRange: [number, number] = cob !== null ? [cob, cob] : [0, 100];
        setInflationProtection({
          type: 'separate',
          combined: [Math.min(aobRange[0], cobRange[0]), Math.max(aobRange[1], cobRange[1])],
          aob: aobRange,
          cob: cobRange,
        });
      } else {
        setInflationProtection(null);
      }
    }
  }

  function handleReset() {
    setDraftBenefit('');
    setDraftDurationType('combined');
    setDraftDuration('');
    setDraftAobDuration('');
    setDraftCobDuration('');
    setDraftInflationType('combined');
    setDraftInflation('');
    setDraftAobInflation('');
    setDraftCobInflation('');
    setMonthlyBenefit(null);
    setBenefitDuration(null);
    setInflationProtection(null);
  }

  return (
    <div className="space-y-4">
      {/* Monthly Benefit */}
      <div className="space-y-2">
        <Label className="text-primary-dark text-sm font-semibold">Monthly Benefit</Label>
        <div className="relative">
          <span className="text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2 text-sm">
            $
          </span>
          <Input
            type="number"
            min={1}
            className="pl-6"
            value={draftBenefit}
            onChange={event => setDraftBenefit(event.target.value)}
            onWheel={e => e.currentTarget.blur()}
            placeholder="e.g. 6000"
            data-testid="benefit-design-filters-monthly-benefit"
          />
        </div>
        <p className="text-muted-foreground text-xs">
          Desired LTC coverage per month. Carriers outside their premium range will be excluded.
        </p>
      </div>

      {/* Benefit Duration */}
      <div className="border-border space-y-3 border-t pt-4">
        <div className="flex items-center justify-between gap-2">
          <Label className="text-primary-dark text-sm font-semibold">Benefit Duration</Label>
          <Select
            value={draftDurationType}
            onValueChange={value => setDraftDurationType(value as 'combined' | 'separate')}
          >
            <SelectTrigger
              size="sm"
              className="h-8! w-max px-2 py-1.5 text-xs [&>svg]:size-3.5"
              data-testid="benefit-design-filters-duration-mode"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="combined">Total Duration</SelectItem>
              <SelectItem value="separate">Separate AOB/COB</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {draftDurationType === 'combined' ? (
          <div className="space-y-1.5">
            <div className="relative">
              <Input
                type="text"
                inputMode="text"
                value={draftDuration}
                onChange={event => setDraftDuration(event.target.value)}
                placeholder='e.g. 3 or "Lifetime"'
                data-testid="benefit-design-filters-duration"
              />
              {!isLifetime(draftDuration) && (
                <span className="text-muted-foreground absolute right-3 top-1/2 -translate-y-1/2 text-sm">
                  yrs
                </span>
              )}
            </div>
            <p className="text-muted-foreground text-xs">
              1–8 years, or type "Lifetime" for unlimited coverage.
            </p>
          </div>
        ) : (
          <div className="space-y-1.5">
            <div className="flex gap-3">
              <div className="flex-1 space-y-1">
                <Label className="text-muted-foreground text-xs">AOB</Label>
                <div className="relative">
                  <Input
                    type="number"
                    min={1}
                    max={8}
                    value={draftAobDuration}
                    onChange={event => setDraftAobDuration(event.target.value)}
                    onWheel={e => e.currentTarget.blur()}
                    placeholder="yrs"
                    data-testid="benefit-design-filters-duration-aob"
                  />
                  <span className="text-muted-foreground absolute right-3 top-1/2 -translate-y-1/2 text-xs">
                    yrs
                  </span>
                </div>
              </div>
              <div className="flex-1 space-y-1">
                <Label className="text-muted-foreground text-xs">COB</Label>
                <div className="relative">
                  <Input
                    type="number"
                    min={1}
                    max={8}
                    value={draftCobDuration}
                    onChange={event => setDraftCobDuration(event.target.value)}
                    onWheel={e => e.currentTarget.blur()}
                    placeholder="yrs"
                    data-testid="benefit-design-filters-duration-cob"
                  />
                  <span className="text-muted-foreground absolute right-3 top-1/2 -translate-y-1/2 text-xs">
                    yrs
                  </span>
                </div>
              </div>
            </div>
            <p className="text-muted-foreground text-xs">AOB and COB durations each 1–8 years.</p>
          </div>
        )}
      </div>

      {/* Inflation Protection */}
      <div className="border-border space-y-3 border-t pt-4">
        <div className="flex items-center justify-between gap-2">
          <Label className="text-primary-dark text-sm font-semibold">Inflation Protection</Label>
          <Select
            value={draftInflationType}
            onValueChange={value => setDraftInflationType(value as 'combined' | 'separate')}
          >
            <SelectTrigger
              size="sm"
              className="h-8! w-max px-2 py-1.5 text-xs [&>svg]:size-3.5"
              data-testid="benefit-design-filters-inflation-mode"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="combined">Combined</SelectItem>
              <SelectItem value="separate">Separate AOB/COB</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {draftInflationType === 'combined' ? (
          <div className="space-y-1.5">
            <div className="relative">
              <Input
                type="number"
                min={0}
                max={5}
                value={draftInflation}
                onChange={event => setDraftInflation(event.target.value)}
                onWheel={e => e.currentTarget.blur()}
                placeholder="% (e.g. 3)"
                data-testid="benefit-design-filters-inflation"
              />
              <span className="text-muted-foreground absolute right-3 top-1/2 -translate-y-1/2 text-sm">
                %
              </span>
            </div>
            <p className="text-muted-foreground text-xs">
              0–5%. Common values: 0%, 3% Compound, 5% Compound.
            </p>
          </div>
        ) : (
          <div className="space-y-1.5">
            <div className="flex gap-3">
              <div className="flex-1 space-y-1">
                <Label className="text-muted-foreground text-xs">AOB</Label>
                <div className="relative">
                  <Input
                    type="number"
                    min={0}
                    max={5}
                    value={draftAobInflation}
                    onChange={event => setDraftAobInflation(event.target.value)}
                    onWheel={e => e.currentTarget.blur()}
                    placeholder="%"
                    data-testid="benefit-design-filters-inflation-aob"
                  />
                  <span className="text-muted-foreground absolute right-3 top-1/2 -translate-y-1/2 text-xs">
                    %
                  </span>
                </div>
              </div>
              <div className="flex-1 space-y-1">
                <Label className="text-muted-foreground text-xs">COB</Label>
                <div className="relative">
                  <Input
                    type="number"
                    min={0}
                    max={5}
                    value={draftCobInflation}
                    onChange={event => setDraftCobInflation(event.target.value)}
                    onWheel={e => e.currentTarget.blur()}
                    placeholder="%"
                    data-testid="benefit-design-filters-inflation-cob"
                  />
                  <span className="text-muted-foreground absolute right-3 top-1/2 -translate-y-1/2 text-xs">
                    %
                  </span>
                </div>
              </div>
            </div>
            <p className="text-muted-foreground text-xs">
              Enter AOB and COB inflation rates separately (0–5%).
            </p>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="border-border flex flex-col gap-2 border-t pt-4">
        <Button
          onClick={handleApply}
          data-testid="benefit-design-filters-apply"
        >
          Apply
        </Button>
        <Button
          variant="outline"
          onClick={handleReset}
          data-testid="benefit-design-filters-reset"
        >
          Reset
        </Button>
      </div>
    </div>
  );
}
